import { getErrorMessageAsync } from 'src/redux/pull-request/sagas/utils/get-error-message';
import urls from 'src/redux/pull-request/urls';
import authRequest from 'src/utils/fetch';

/**
 * Sends a request to create a label on a pull request. Throws error on failure
 * @param owner workspace
 * @param slug repository
 * @param prId pull request id
 * @param labelContent label content of new label
 * @returns response body in json format
 */
export async function sendCreatePullRequestLabelRequest(
  owner: string,
  slug: string,
  labelContent: string,
  prId?: number
) {
  let isError = false;
  let label = [];
  let errorMessage = '';

  try {
    if (prId) {
      // Calling labels is to hit the endpoint to create a new label and attach to this pr
      const url = urls.api.internal.prLabels(owner, slug, prId);
      const method = 'POST';
      const body = JSON.stringify({ content: labelContent });
      const authUrl = authRequest(url, {
        method,
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const response = await fetch(authUrl);
      label = await response.json();

      if (!response.ok) {
        throw Error(label.error.message);
      }
    } else {
      throw new Error(
        'Tried to add label to a Pull Request when missing Pull Request'
      );
    }
  } catch (e) {
    // response had error
    isError = true;
    errorMessage = e.message;
  }
  return { isError, label, errorMessage };
}

/**
 * Sends a request to add an existing label to a pull request. Throws error on failure
 * @param owner workspace
 * @param slug repository
 * @param prId pull request id
 * @param labelContent label content of the original label
 * @returns response body in json format
 */
export async function sendUpdatePullRequestLabelRequest(
  owner: string,
  slug: string,
  labelContent: string,
  prId?: number
) {
  let isError = false;
  let label = [];
  let errorMessage = '';

  try {
    if (prId) {
      // Calling label is to hit the endpoint to update an existing label and add it to this pr
      const url = urls.api.internal.prLabel(owner, slug, prId, labelContent);
      const method = 'PUT';
      const body = JSON.stringify({});
      const authUrl = authRequest(url, {
        method,
        body,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const response = await fetch(authUrl);
      label = await response.json();

      if (!response.ok) {
        throw Error(label.error.message);
      }
    } else {
      throw new Error(
        'Tried to add label to a Pull Request when missing Pull Request'
      );
    }
  } catch (e) {
    // response had error
    isError = true;
    errorMessage = e.message;
  }
  return { isError, label, errorMessage };
}

/**
 * Sends a request to remove a label from a pull request.
 * @param owner workspace
 * @param slug repository
 * @param prId pull request id
 * @param labelContent id of label to remove
 * @returns isError if error
 */
export async function sendRemovePullRequestLabelRequest(
  owner: string,
  slug: string,
  prId: number | undefined,
  labelContent: string
) {
  let isError = false;

  try {
    if (prId) {
      const url = urls.api.internal.prLabel(owner, slug, prId, labelContent);
      const method = 'DELETE';
      const authUrl = authRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const response = await fetch(authUrl);

      if (!response.ok) {
        const message: string = await getErrorMessageAsync(response);
        const error = new Error(message);
        // @ts-ignore
        error.status = response.status;
        throw error;
      }
    } else {
      throw new Error(
        'Tried to remove a label from a Pull Request when missing ID or Pull Request'
      );
    }
  } catch (e) {
    // response had error
    isError = true;
  }
  return { isError };
}

/**
 * Sends a request to get the labels from a pull request
 * @param owner workspace
 * @param slug repository
 * @param prid pull request id
 * @returns isError if error
 * @returns response body in json format
 */
export async function sendGetPullRequestLabelsRequest(
  owner: string,
  slug: string,
  prId: number | undefined
) {
  let isError = false;
  let labels = [];

  try {
    if (prId) {
      const url = urls.api.internal.prLabels(owner, slug, prId);
      const method = 'GET';
      const authUrl = authRequest(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const response = await fetch(authUrl);
      labels = await response.json();

      if (!response.ok) {
        throw Error(labels.error.message);
      }
    } else {
      throw new Error(
        'Tried to get labels from a Pull Request when missing Pull Request'
      );
    }
  } catch (e) {
    // response had error
    isError = true;
  }
  return { isError, labels };
}

/**
 * Sends a request to get all the labels from a workspace
 * @param owner workspace
 * @returns isError if error
 * @returns response body in json format
 */
export async function sendGetWorkspacePullRequestLabelsRequest(owner: string) {
  let isError = false;
  let labels = [];

  try {
    const url = urls.api.internal.labels(owner);
    const method = 'GET';
    const authUrl = authRequest(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const response = await fetch(authUrl);
    labels = await response.json();

    if (!response.ok) {
      throw Error(labels.error.message);
    }
  } catch (e) {
    // response had error
    isError = true;
  }
  return { isError, labels };
}
